<template>
	<v-container fluid class="pa-0">
	  <!-- Toast notification component -->
	  <v-snackbar
		v-model="showSnackbar"
		:color="snackbarColor"
		:timeout="3000"
		location="top"
		rounded="pill"
		elevation="4"
		class="pa-3"
	  >
		<div class="d-flex align-center">
		  <v-icon class="me-3" :icon="snackbarIcon"></v-icon>
		  <span class="text-subtitle-1 font-weight-medium">{{ snackbarText }}</span>
		</div>
		<template v-slot:actions>
		  <v-btn
			variant="text"
			icon="mdi-close"
			@click="showSnackbar = false"
		  ></v-btn>
		</template>
	  </v-snackbar>
  
	  <!-- Header section with gradient background -->
	  <v-sheet 
		color="primary" 
		class="py-6 mb-6" 
		rounded="0"
		elevation="2"
	  >
		<v-container>
		  <v-row justify="center" class="text-center">
			<v-col cols="12">
			  <div class="d-flex align-center justify-center">
				<v-icon icon="mdi-clipboard-list" size="42" color="white" class="me-3"></v-icon>  
				<h1 class="text-h4 font-weight-medium text-white mb-0">Interview Personas</h1>
			  </div>
			  <div class="text-subtitle-1 text-white-darken-1 mt-2">Create and manage interview scenarios with AI-powered personas</div>
			</v-col>
		  </v-row>
		</v-container>
	  </v-sheet>
  
	  <v-container>
		<!-- Topic Selection Card -->
		<v-card rounded="lg" class="mb-6" variant="outlined" elevation="1">
		  <v-card-item>
			<template v-slot:prepend>
			  <v-icon icon="mdi-filter-variant" color="primary" size="24"></v-icon>
			</template>
			<v-card-title class="text-h6 ps-1">Select Topic</v-card-title>
			<v-card-subtitle class="ps-1">Choose a topic to generate interview questions</v-card-subtitle>
		  </v-card-item>
		  
		  <v-card-text>
			<v-row class="align-center">
			  <v-col cols="12" md="9">
				<v-autocomplete
				  v-show="!isCampaignsLoading"
				  v-model="selectedCampaign"
				  :items="dropdownCampaigns"
				  label="Select a topic"
				  variant="outlined"
				  density="comfortable"
				  hide-details
				  clearable
				  class="rounded-lg"
				>
				  <template v-slot:prepend-item>
					<v-list-item prepend-icon="mdi-filter-variant" title="Topics"></v-list-item>
					<v-divider></v-divider>
				  </template>
				</v-autocomplete>
				
				<div v-show="isCampaignsLoading" class="text-center mt-4">
				  <v-progress-circular indeterminate color="primary" class="mb-2" />
				  <p class="text-body-2 text-disabled">Loading topics...</p>
				</div>
			  </v-col>
			  
			  <v-col cols="12" md="3">
				<v-btn
				  @click="openDialog"
				  color="secondary"
				  class="py-2"
				  size="large"
				  rounded="pill"
				  elevation="1"
				  v-tooltip.bottom="'Add a new topic to the list'"
				  block
				  variant="tonal"
				>
				  <v-icon start icon="mdi-plus-circle-outline" class="me-1"></v-icon>
				  Add New Topic
				</v-btn>
			  </v-col>
			</v-row>
		  </v-card-text>
		</v-card>
  
		<!-- Action Buttons -->
		<v-card rounded="lg" class="mb-6" variant="outlined" elevation="1">
		  <v-card-item>
			<template v-slot:prepend>
			  <v-icon icon="mdi-lightning-bolt" color="primary" size="24"></v-icon>
			</template>
			<v-card-title class="text-h6 ps-1">Actions</v-card-title>
			<v-card-subtitle class="ps-1">Generate questions or start interviews</v-card-subtitle>
		  </v-card-item>
		  
		  <v-card-text>
			<v-row class="align-center" justify="space-between">
			  <v-col cols="12" sm="4" class="my-2">
				<v-btn
				  :loading="isLoading_questions"
				  :disabled="!selectedCampaign || isLoading_interview || isLoading_fg" 
				  @click="generateQuestions"
				  color="primary"
				  class="py-2"   
				  size="large"
				  rounded="pill"
				  elevation="1"   
				  v-tooltip.bottom="'Generate questions for the selected topic'" 
				  block    
				>
				  <v-icon start class="me-2">mdi-account-question-outline</v-icon> 
				  Generate Questions
				</v-btn>
			  </v-col>
  
			  <v-col cols="12" sm="4" class="my-2">
				<v-btn
				  :loading="isLoading_fg"
				  :disabled="selectedCheckboxes.length === 0 || !selectedCampaign || isLoading_interview" 
				  @click="startFocusGroup"
				  color="info"
				  class="py-2"   
				  size="large"
				  rounded="pill"
				  elevation="1"   
				  v-tooltip.bottom="'Run a focus group with the selected personas'" 
				  block    
				>
				  <v-icon start class="me-2">mdi-account-group</v-icon> 
				  Start Focus Group
				</v-btn>
			  </v-col>
  
			  <v-col cols="12" sm="4" class="my-2">
				<v-btn
				  :loading="isLoading_interview"
				  :disabled="selectedCheckboxes.length === 0 || !selectedCampaign || isLoading_fg" 
				  @click="startInterview"
				  color="success"
				  class="py-2"   
				  size="large"
				  rounded="pill"
				  elevation="1"
				  v-tooltip.bottom="'Interview the selected personas'"
				  block         
				>
				  <v-icon start class="me-2">mdi-account-voice</v-icon> 
				  Start Interview
				</v-btn>
			  </v-col>
			</v-row>
		  </v-card-text>
		</v-card>
  
		<!-- Main Content Area -->
		<v-row>
		  <!-- Personas Selection -->
		  <v-col cols="12" md="8">
			<v-card rounded="lg" class="h-100" elevation="1">
			  <v-card-item>
				<template v-slot:prepend>
				  <v-icon icon="mdi-account-multiple-outline" color="primary" size="24"></v-icon>
				</template>
				<v-card-title class="text-h6 ps-1">Choose Personas</v-card-title>
				<v-card-subtitle class="ps-1">Select personas to include in the interview process</v-card-subtitle>
			  </v-card-item>
			  
			  <v-card-text style="max-height: 350px; overflow-y: auto;" class="pt-0">
				<div class="d-flex justify-end mb-2">
				  <v-btn
					size="small"
					@click="toggleCheckAll"
					:color="buttonLabel === 'Deselect All' ? 'error' : 'secondary'"
					class="text-none"
					variant="text"
				  >
					<v-icon start size="small" class="me-1">{{ buttonLabel === 'Deselect All' ? 'mdi-close-circle-outline' : 'mdi-check-circle-outline' }}</v-icon>
					{{ buttonLabel }}
				  </v-btn>
				</div>
				
				<v-chip-group
				  v-model="selectedCheckboxes"
				  column
				  multiple
				  class="persona-chips"
				>
				  <v-chip
					v-for="(item, index) in checkboxOptions"
					:key="index"
					:value="item"
					color="primary"
					variant="outlined"
					class="mb-3 pa-2 rounded-lg"
					size="large"
					filter
				  >
					<v-avatar start class="me-2">
					  <v-img :src="`https://randomuser.me/api/portraits/lego/${(index % 8) + 1}.jpg`"></v-img>
					</v-avatar>
					{{ item }}
				  </v-chip>
				</v-chip-group>
				
				<v-alert
				  v-if="checkboxOptions.length === 0"
				  type="info"
				  variant="tonal"
				  class="mt-4"
				>
				  No personas available. Please check your configuration.
				</v-alert>
			  </v-card-text>
			</v-card>
		  </v-col>
		  
		  <!-- Topic Description -->
		  <v-col cols="12" md="4">
			<v-card rounded="lg" class="h-100" elevation="1">
			  <v-card-item>
				<template v-slot:prepend>
				  <v-icon icon="mdi-text-box-outline" color="primary" size="24"></v-icon>
				</template>
				<v-card-title class="text-h6 ps-1">Topic Description</v-card-title>
				<v-card-subtitle class="ps-1">Details about the selected topic</v-card-subtitle>
			  </v-card-item>
			  
			  <v-card-text class="pt-0">
				<v-alert
				  v-if="!selectedCampaign"
				  type="info"
				  variant="tonal"
				>
				  Please select a topic to view its description.
				</v-alert>
				<div v-else class="topic-description">
				  {{ currentCampaignDescription }}
				</div>
			  </v-card-text>
			</v-card>
		  </v-col>
		</v-row>
	  </v-container>
  
	  <!-- Dialogs -->
	  <CampaignDialog
		v-model:showDialog="isAddDialogVisible"
		@submit="addCampaign"
	  />
  
	  <v-dialog v-model="isQuestionDialogVisible" width="700px">
		<v-card class="rounded-xl elevation-5">
		  <v-card-item class="bg-primary text-white rounded-t-xl">
			<template v-slot:prepend>
			  <v-icon icon="mdi-note-plus-outline" color="white"></v-icon>
			</template>
			<v-card-title class="text-h5 ps-1">Generated Questions</v-card-title>
			
			<template v-slot:append>
			  <v-btn
				v-if="!isEditing"
				icon
				variant="text"
				color="white"
				@click="startEditing"
				class="ml-2"
			  >
				<v-icon>mdi-pencil</v-icon>
			  </v-btn>
			</template>
		  </v-card-item>
  
		  <v-card-text class="pa-4">
			<!-- Display mode -->
			<div v-if="generated_questions && !isEditing" class="py-4 white-space-pre-wrap">
			  {{ generated_questions }}
			</div>
			
			<!-- Edit mode -->
			<v-textarea
			  v-else-if="isEditing"
			  v-model="editedQuestions"
			  rows="10"
			  auto-grow
			  variant="outlined"
			  class="py-2"
			></v-textarea>
			
			<!-- Empty state -->
			<div v-else class="text-center py-4">
			  <v-icon icon="mdi-text-box-search-outline" size="48" color="grey-lighten-1" class="mb-2"></v-icon>
			  <p class="text-body-1 text-grey-darken-1">No questions generated yet.</p>
			</div>
		  </v-card-text>
  
		  <v-card-actions class="pa-4">
			<v-spacer></v-spacer>
			
			<!-- Cancel button (always visible) -->
			<v-btn 
			  @click="isEditing ? cancelEditing() : closeQuestionsDialog()" 
			  color="grey-darken-1" 
			  variant="text"
			  rounded="pill" 
			  class="px-5"
			>
			  {{ isEditing ? 'Cancel' : 'Close' }}
			</v-btn>
			
			<!-- Save button (visible in edit mode) -->
			<v-btn 
			  v-if="isEditing"
			  @click="saveEdits" 
			  color="success" 
			  variant="elevated"  
			  rounded="pill"
			  class="px-5"
			>
			  <v-icon start icon="mdi-content-save" class="me-1"></v-icon> Save Changes
			</v-btn>
			
			<!-- Start Interview button (visible in view mode) -->
			<v-btn 
			  v-else
			  @click="startInterview" 
			  color="primary" 
			  variant="elevated"  
			  rounded="pill"
			  class="px-5"
			  :disabled="selectedCheckboxes.length === 0"
			>
			  <v-icon start icon="mdi-play-circle-outline" class="me-1"></v-icon> Start Interview
			</v-btn>
		  </v-card-actions>
		</v-card>      
	  </v-dialog>
	</v-container>
  </template>
  
  <script>
  import { start_interview, fetch_campaigns, add_campaign, fetch_persona_names, start_focus_group, generate_interview_questions} from "../api.js";
  import CampaignDialog from "./InterviewCampaignDialog.vue";
  
  export default {
	components: {
	  CampaignDialog,
	},
	data() {
	  return {
		campaigns: [], // Stores the list of campaigns fetched from the backend
		selectedCampaign: null, // Currently selected campaign
		selectedCheckboxes: [], // Selected personas
		checkboxOptions: [], // Available personas
		isAddDialogVisible: false, // Controls the visibility of the dialog
		isLoading_interview: false, // To control the loading state of the button
		isLoading_fg: false,
		isLoading_questions: false,
		isCampaignsLoading: false, // Loading state for campaigns
		personas: null,
		generated_questions: '',
  
		// questions dialog
		isQuestionDialogVisible: false,
		isEditing: false,
		editedQuestions: '',
  
		// Toast notification
		showSnackbar: false,
		snackbarText: "",
		snackbarColor: "error",
		snackbarIcon: "mdi-alert-circle",
	  };
	},
	computed: {
	  // Populate dropdownCampaigns with the names of campaigns
	  dropdownCampaigns() {
      return this.campaigns.map((campaign) => campaign.campaign_name);
    },
    currentCampaignDescription() {
      const campaign = this.campaigns.find(campaign => campaign.campaign_name === this.selectedCampaign);
      return campaign ? campaign.description : '';
    },
    buttonLabel() {
      return this.selectedCheckboxes.length === this.checkboxOptions.length
        ? "Deselect All"
        : "Select All";
    },
  },
  methods: {
    // Open dialog and initialize
    openQuestionsDialog() {
      this.isQuestionDialogVisible = true;
      this.isEditing = false;
    },
    
    // Close dialog
    closeQuestionsDialog() {
      this.isQuestionDialogVisible = false;
      this.isEditing = false;
    },
    
    // Start editing mode
    startEditing() {
      this.editedQuestions = this.generated_questions;
      this.isEditing = true;
    },
    
    // Cancel editing and revert changes
    cancelEditing() {
      this.isEditing = false;
      this.editedQuestions = '';
    },
    
    // Save edits and update generated_questions
    saveEdits() {
      this.generated_questions = this.editedQuestions;
      this.isEditing = false;
    },

    // Show a toast notification with a message and optional type
    showToast(message, type = "error") {
      this.snackbarText = message;
      
      // Set appropriate color and icon based on notification type
      if (type === "error") {
        this.snackbarColor = "error"; // Reddish color for errors
        this.snackbarIcon = "mdi-alert-circle";
      } else if (type === "success") {
        this.snackbarColor = "success"; // Green color for success
        this.snackbarIcon = "mdi-check-circle";
      } else if (type === "warning") {
        this.snackbarColor = "warning"; // Amber/yellow for warnings
        this.snackbarIcon = "mdi-alert";
      } else if (type === "info") {
        this.snackbarColor = "info"; // Blue color for information
        this.snackbarIcon = "mdi-information";
      }
      
      this.showSnackbar = true;
    },

    toggleCheckAll() {
      if (this.selectedCheckboxes.length === this.checkboxOptions.length) {
        this.selectedCheckboxes = []; // Deselect all
      } else {
        this.selectedCheckboxes = [...this.checkboxOptions]; // Select all
      }
    },
    
    openDialog() {
      this.isAddDialogVisible = true; // Open the dialog
    },
    
    async fetchCampaigns() {
      this.isCampaignsLoading = true; // Show a loading state
      try {
        const response = await fetch_campaigns(); // API call
        if (response && Array.isArray(response.data)) {
          this.campaigns = response.data;
          console.log("Fetched campaigns:", this.campaigns);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching campaigns:", error);
        this.campaigns = []; // Default to an empty list to avoid null references
        this.showToast("Failed to load topics. Please try again.", "error");
      } finally {
        this.isCampaignsLoading = false; // Hide the loading state
      }
    },
    
    async fetchPersonaNames() {
      try {
        const response = await fetch_persona_names(); // API call to fetch persona names
        if (response && Array.isArray(response.data)) {
          this.checkboxOptions = response.data; // Update checkboxOptions with fetched persona names
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching personas:", error);
        this.checkboxOptions = []; // Fallback to empty list
        this.showToast("Failed to load personas. Please try again.", "error");
      }
    },
    
    async addCampaign(campaignData) {
      try {
        console.log("Submitting campaign:", campaignData);

        // Call the backend API to add the campaign
        const response = await add_campaign(
          campaignData.campaign_name,
          campaignData.description
        );

        console.log("Topic added successfully:", response.data);
        this.showToast("Topic added successfully!", "success");

        // Optionally reload campaigns or update the dropdown
        await this.fetchCampaigns();
      } catch (error) {
        console.error("Error adding campaign:", error);
        this.showToast("Failed to add topic. Please try again.", "error");
      }
    },

    async generateQuestions() {
      this.generated_questions = "";
      this.isLoading_questions = true;
      try {
        if (!this.selectedCampaign) {
          this.showToast("Please select a topic first.", "warning");
          return;
        }

        // Find the selected campaign by name
        const campaign = this.campaigns.find(
          (campaign) => campaign.campaign_name === this.selectedCampaign
        );

        // Prepare the payload
        const payload = {
          campaign_name: this.selectedCampaign,
          query: campaign ? campaign.description : "",
          campaign_id: campaign.campaign_id,
        };

        // Call the backend API
        const response = await generate_interview_questions(
          payload.campaign_name,
          payload.query,
          payload.campaign_id
        );
        this.generated_questions = response.data.questions;
        console.log("Questions generated successfully:", response.data);
        this.showToast("Questions generated successfully!", "success");
      } catch (error) {
        if (error.response) {
          console.error("Validation Error:", error.response.data);
          this.showToast(`Validation Error: ${error.response.data.detail}`, "error");
        } else {
          console.error("Error during question generation:", error);
          this.showToast("Failed to generate questions. Please try again.", "error");
        }
      } finally {
        this.isLoading_questions = false; // Reset loading state
        if (this.generated_questions) {
          this.isQuestionDialogVisible = true; // Show the dialog with the questions
        }
      }
    },

    async startInterview() {
      this.isLoading_interview = true;
	  this.isQuestionDialogVisible = false
      try {
        if (!this.selectedCampaign) {
          this.showToast("Please select a topic first.", "warning");
          return;
        }
        if (this.selectedCheckboxes.length === 0) {
          this.showToast("Please select at least one persona.", "warning");
          return;
        }

        // Find the selected campaign by name
        const campaign = this.campaigns.find(
          (campaign) => campaign.campaign_name === this.selectedCampaign
        );

        // Prepare the payload
        const payload = {
          campaign_name: this.selectedCampaign,
          query: campaign ? campaign.description : "",
          personas_list: [...this.selectedCheckboxes],
          campaign_id: campaign.campaign_id,
        };

        // Add generated_questions to the payload if it's not empty
        if (this.generated_questions) {
          payload.generated_questions = this.generated_questions;
        }

        // Log the payload for debugging
        console.log("Payload being sent:", payload);

        // Call the backend API
        const response = await start_interview(
          payload.campaign_name,
          payload.query,
          payload.personas_list,
          payload.campaign_id,
          payload.generated_questions
        );
        console.log("Interview started successfully:", response.data);
        this.showToast("Interview started successfully!", "success");
        this.closeQuestionsDialog(); // Close the questions dialog if open
      } catch (error) {
        if (error.response) {
          console.error("Validation Error:", error.response.data);
          this.showToast(`Validation Error: ${error.response.data.detail}`, "error");
        } else {
          console.error("Error during start interview:", error);
          this.showToast("Failed to start the interview. Please try again.", "error");
        }
      } finally {
        this.isLoading_interview = false; // Reset loading state
      }
    },
    
    async startFocusGroup() {
      this.isLoading_fg = true;
      try {
        if (!this.selectedCampaign) {
          this.showToast("Please select a topic first.", "warning");
          return;
        }
        if (this.selectedCheckboxes.length === 0) {
          this.showToast("Please select at least one persona.", "warning");
          return;
        }
        
        // Find the selected campaign by name
        const campaign = this.campaigns.find(
          (campaign) => campaign.campaign_name === this.selectedCampaign
        );
        
        // Prepare payload
        const payload = {
          query: campaign ? campaign.description : "",
          personas_list: [...this.selectedCheckboxes],
          campaign_name: this.selectedCampaign,
          campaign_id: campaign.campaign_id,
        };
        
        console.log("Payload being sent:", payload);
        
        // Call API to start the focus group
        const response = await start_focus_group(
          payload.query,
          payload.personas_list,
          payload.campaign_name,
          payload.campaign_id
        );
        
        console.log("Focus group started successfully:", response.data);
        this.showToast("Focus group started successfully!", "success");
        
        // Optionally, refresh the focus groups list after starting one
        await this.fetchFocusGroups();
      } catch (error) {
        if (error.response) {
          console.error("Validation Error:", error.response.data);
          this.showToast(`Validation Error: ${error.response.data.detail}`, "error");
        } else {
          console.error("Error during start focus group:", error);
          this.showToast("Failed to start the focus group. Please try again.", "error");
        }
      } finally {
        this.isLoading_fg = false;
      }
    },
    
    // Fetch all focus groups from the backend
    async fetchFocusGroups() {
      try {
        const response = await fetch_focus_groups();
        if (response && Array.isArray(response.data)) {
          this.focusGroups = response.data;
          console.log("Fetched focus groups:", this.focusGroups);
        } else {
          throw new Error("Invalid response format");
        }
      } catch (error) {
        console.error("Error fetching focus groups:", error);
        this.focusGroups = [];
      }
    },
  },
  
  async created() {
    try {
      await this.fetchCampaigns();
      await this.fetchPersonaNames(); // Fetch persona names when component loads
    } catch (error) {
      console.error("Error initializing component:", error);
      this.showToast("Failed to load initial data. Please refresh the page.", "error");
    }
  }
};
</script>

<style scoped>
.align-stretch {
  align-items: stretch;
}

.h-100 {
  height: 100%;
}

/* This class preserves whitespace including newlines */
.white-space-pre-wrap {
  white-space: pre-wrap;
}

/* Styling for the topic description */
.topic-description {
  font-size: 15px;
  line-height: 1.6;
  color: rgba(0, 0, 0, 0.87);
  padding: 8px 4px;
}

/* Styling for persona chips */
.persona-chips .v-chip--selected {
  border-color: var(--v-primary-base) !important;
  background-color: rgba(var(--v-primary-base), 0.1) !important;
}

/* Add subtle hover effect to cards */
.v-card {
  transition: transform 0.2s, box-shadow 0.2s;
}

.v-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.1) !important;
}
</style>